import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { Layout } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Edit, Trash2, Tags } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CategoriesPage = () => {
  const { chapter } = useAuth();
  const [categories, setCategories] = useState([]);
  const [powerTeams, setPowerTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: "", power_team_id: "", status: "active" });

  const token = localStorage.getItem("token");
  const primaryColor = chapter?.primary_color || "#CC0000";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [catRes, ptRes] = await Promise.all([
        fetch(`${API}/categories`, { headers }),
        fetch(`${API}/power-teams`, { headers })
      ]);
      if (catRes.ok) setCategories(await catRes.json());
      if (ptRes.ok) setPowerTeams(await ptRes.json());
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editing ? `${API}/categories/${editing.category_id}` : `${API}/categories`;
      const response = await fetch(url, {
        method: editing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editing ? "Category updated" : "Category created");
        setDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        toast.error("Operation failed");
      }
    } catch (error) {
      toast.error("Error saving category");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Deactivate this category?")) return;
    try {
      const response = await fetch(`${API}/categories/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Category deactivated");
        fetchData();
      }
    } catch (error) {
      toast.error("Error deactivating");
    }
  };

  const resetForm = () => {
    setFormData({ name: "", power_team_id: "", status: "active" });
    setEditing(null);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setFormData({ name: cat.name, power_team_id: cat.power_team_id || "", status: cat.status });
    setDialogOpen(true);
  };

  const getPowerTeamName = (id) => powerTeams.find(pt => pt.power_team_id === id)?.name || "Unassigned";

  // Group categories by power team
  const groupedCategories = categories.reduce((acc, cat) => {
    const ptName = getPowerTeamName(cat.power_team_id);
    if (!acc[ptName]) acc[ptName] = [];
    acc[ptName].push(cat);
    return acc;
  }, {});

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="categories-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-slate-900">Categories</h1>
            <p className="text-muted-foreground mt-1">{categories.length} categories across {powerTeams.length} power teams</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button style={{ backgroundColor: primaryColor }} data-testid="add-category-btn">
                <Plus className="mr-2 h-4 w-4" />
                Add Category
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editing ? "Edit Category" : "Add Category"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Power Team</Label>
                  <Select value={formData.power_team_id} onValueChange={(v) => setFormData({ ...formData, power_team_id: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select power team" />
                    </SelectTrigger>
                    <SelectContent>
                      {powerTeams.filter(pt => pt.status === "active").map(pt => (
                        <SelectItem key={pt.power_team_id} value={pt.power_team_id}>{pt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" style={{ backgroundColor: primaryColor }}>{editing ? "Update" : "Create"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {Object.entries(groupedCategories).map(([ptName, cats]) => (
          <div key={ptName} className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Tags className="h-5 w-5" style={{ color: primaryColor }} />
              {ptName}
              <Badge variant="outline" className="ml-2">{cats.length}</Badge>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {cats.map((cat, index) => (
                <Card 
                  key={cat.category_id} 
                  className="border-0 shadow-sm hover:shadow-md transition-shadow animate-fade-in"
                  style={{ animationDelay: `${index * 0.03}s` }}
                  data-testid={`category-card-${cat.category_id}`}
                >
                  <CardContent className="py-4 px-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-2 h-8 rounded-full"
                          style={{ backgroundColor: cat.status === "active" ? primaryColor : "#94A3B8" }}
                        />
                        <div>
                          <p className="font-medium text-slate-900">{cat.name}</p>
                          <Badge 
                            variant="outline" 
                            className={`text-xs mt-1 ${cat.status === "active" ? "text-green-600" : "text-slate-500"}`}
                          >
                            {cat.status}
                          </Badge>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(cat)}>
                            <Edit className="mr-2 h-4 w-4" />Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(cat.category_id)} className="text-red-600">
                            <Trash2 className="mr-2 h-4 w-4" />Deactivate
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}

        {categories.length === 0 && (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center text-muted-foreground">
              No categories yet. Create power teams first, then add categories.
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default CategoriesPage;
